import React, {useState} from 'react';
import {Link, Navigate, useLocation} from "react-router-dom";
import {LocalStorage} from "./utils/LocalStorage";
import {FaIcon} from "./components/FaIcon";
import {useTranslation} from 'react-i18next';
import {usePreviewContext} from './context/PreviewContext';
import {usePortalModules} from './hooks/usePortalModules';
import logo from './images/logo_dashbo_purple.svg';

const darkenHex = (hex, percent) => {
    const n = parseInt(hex.slice(1), 16);
    const d = Math.round(2.55 * percent);
    return '#' + [n >> 16, (n >> 8) & 0xFF, n & 0xFF]
        .map(c => Math.max(0, c - d).toString(16).padStart(2, '0')).join('');
};

// Navegacion simplificada: solo Tablero y Connect
const PORTAL_NAV_ITEMS = [
    {suffix: '', label: 'portal.nav.dashboard', icon: 'chart-pie', exact: true, module: null},
    {suffix: '/dashbo-connect', label: 'portal.nav.connect', icon: 'microchip', module: 'DASHBO_MCP'},
];

export const LayoutPortal = ({children}) => {
    const {t} = useTranslation();
    const location = useLocation();
    const {isPreview, previewClientId, previewBranding} = usePreviewContext();
    const modules = usePortalModules();
    const [loggedOut, setLoggedOut] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const basePath = isPreview ? `/client/${previewClientId}/preview` : '/portal';
    const showLogout = !isPreview;

    const agencyLogoUrl = isPreview ? previewBranding?.logo_url : LocalStorage.fetchAgencyLogoUrl();
    const agencyDisplayName = isPreview ? previewBranding?.display_name_for_clients : LocalStorage.fetchAgencyDisplayName();

    const brandPrimaryColor = isPreview ? previewBranding?.brand_primary_color : LocalStorage.fetchBrandPrimaryColor();
    const portalStyle = {};
    if (brandPrimaryColor) {
        portalStyle['--portal-accent'] = brandPrimaryColor;
        portalStyle['--portal-accent-hover'] = darkenHex(brandPrimaryColor, 15);
        portalStyle['--portal-accent-bg'] = brandPrimaryColor + '0F';
    }

    const onlyConnect = modules && modules.length === 1 && modules[0] === 'DASHBO_MCP';
    const filteredItems = modules
        ? PORTAL_NAV_ITEMS.filter(item => {
            if (item.module) return modules.includes(item.module);
            if (item.suffix === '' && onlyConnect) return false;
            return true;
        })
        : PORTAL_NAV_ITEMS;

    const navItems = filteredItems.map(item => ({
        ...item,
        path: basePath + item.suffix,
    }));

    const handleLogout = () => {
        LocalStorage.removeUserData();
        setLoggedOut(true);
    };

    if (loggedOut) {
        return <Navigate to="/login" />;
    }

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.path;
        return location.pathname.startsWith(item.path);
    };

    return (
        <div className="portal-layout" style={portalStyle}>
            <header className="portal-topbar">
                <div className="portal-topbar-inner">
                    <div className="portal-topbar-left">
                        <Link to={basePath} className="portal-logo-link">
                            {agencyLogoUrl ? (
                                <img src={agencyLogoUrl} className="portal-logo" alt={agencyDisplayName || 'Logo'} />
                            ) : (
                                <img src={logo} className="portal-logo" alt="Dashbo" />
                            )}
                            <span className="portal-logo-branding">
                                {agencyDisplayName ? (
                                    <>
                                        <span className="portal-logo-dashbo-by">Dashbo</span>
                                        <span className="portal-logo-agency-name">{agencyDisplayName}</span>
                                    </>
                                ) : (
                                    <span className="portal-logo-agency-name">Dashbo</span>
                                )}
                            </span>
                        </Link>

                        <nav className="portal-nav portal-nav-desktop">
                            {navItems.map(item => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`portal-nav-item ${isActive(item) ? '-active' : ''}`}
                                    title={t(item.label)}
                                >
                                    <FaIcon iconName={item.icon} />
                                    <span className="portal-nav-label">{t(item.label)}</span>
                                </Link>
                            ))}
                        </nav>
                    </div>

                    <div className="portal-topbar-right">
                        {showLogout && (
                            <button onClick={handleLogout} className="portal-logout-btn" title={t('portal.nav.logout')}>
                                <FaIcon iconName="power-off" />
                            </button>
                        )}

                        <button
                            className="portal-hamburger"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Menu"
                        >
                            <FaIcon iconName={menuOpen ? 'xmark' : 'bars'} />
                        </button>
                    </div>
                </div>
            </header>

            {menuOpen && (
                <nav className="portal-nav-mobile">
                    {navItems.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`portal-nav-mobile-item ${isActive(item) ? '-active' : ''}`}
                            onClick={() => setMenuOpen(false)}
                        >
                            <FaIcon iconName={item.icon} />
                            <span>{t(item.label)}</span>
                        </Link>
                    ))}
                    {showLogout && (
                        <button
                            className="portal-nav-mobile-item"
                            onClick={handleLogout}
                        >
                            <FaIcon iconName="power-off" />
                            <span>{t('portal.nav.logout')}</span>
                        </button>
                    )}
                </nav>
            )}

            <main className="portal-main">
                <div className="portal-container">
                    {children}
                </div>
            </main>
        </div>
    );
};
